import { useState } from 'react';
import type { CourseOption, PlanCourse, RequirementGroup } from '@teachur/http-client';

import { courseKey, sumCredits } from '../lib/planView';

const CREDIT_OVERAGE_ALLOWANCE = 2;

interface SwitchPicker {
  kind: 'switch';
  groupId: string;
  categoryId: number | null;
  replacing: string;
  label: string;
}
interface AddPicker {
  kind: 'add';
  groupId: string;
  label: string;
  remainingCredits: number;
}
type PickerState = SwitchPicker | AddPicker;

export interface PlanPickerModalProps {
  isOpen: boolean;
  title: string;
  groupLabel: string;
  categoryId?: number;
  multiple: boolean;
  remainingCredits: number | null;
}

interface UsePlanPickerArgs {
  onSwitch: (groupId: string, replacingKey: string, option: CourseOption) => void;
  onAdd: (groupId: string, options: CourseOption[]) => void;
}

export const usePlanPicker = ({ onSwitch, onAdd }: UsePlanPickerArgs) => {
  const [picker, setPicker] = useState<PickerState | null>(null);

  const close = () => setPicker(null);

  const openSwitch = (group: RequirementGroup, course: PlanCourse) =>
    setPicker({
      kind: 'switch',
      groupId: group.id,
      categoryId: group.categoryId,
      replacing: courseKey(course),
      label: group.title,
    });

  const openAdd = (group: RequirementGroup) =>
    setPicker({
      kind: 'add',
      groupId: group.id,
      label: group.title,
      remainingCredits: Math.max(
        0,
        group.requiredCredits + CREDIT_OVERAGE_ALLOWANCE - sumCredits(group.courses)
      ),
    });

  const confirm = (selected: CourseOption[]) => {
    if (!picker) {
      return;
    }
    if (picker.kind === 'switch') {
      const [replacement] = selected;
      if (replacement) {
        onSwitch(picker.groupId, picker.replacing, replacement);
      }
    } else {
      onAdd(picker.groupId, selected);
    }
    close();
  };

  const isSwitch = picker?.kind === 'switch';
  const modalProps: PlanPickerModalProps = {
    isOpen: picker !== null,
    title: isSwitch ? 'Switch course' : 'Add Course',
    groupLabel: picker?.label ?? '',
    categoryId: isSwitch ? (picker.categoryId ?? undefined) : undefined,
    multiple: !isSwitch,
    remainingCredits: picker?.kind === 'add' ? picker.remainingCredits : null,
  };

  return { openSwitch, openAdd, confirm, close, modalProps };
};
